import { useEffect, useRef, useState } from 'react';
import type { BreakdownSubItem, BreakdownSubItemInput } from '@jobos/shared';
import { useEquipmentCatalog } from '@/hooks/useEquipmentCatalog';
import { type SubItemForm } from '../breakdown/subItemTypes';
import { EquipmentEditor } from './EquipmentEditor';

interface EquipmentLineItemBodyProps {
  subItems: SubItemForm[];
  onSubItemsChange: (next: SubItemForm[]) => void;
  readOnly?: boolean;
  lineItemDescription?: string | null;
}

function toBreakdownSubItems(forms: SubItemForm[]): BreakdownSubItem[] {
  return forms.map(
    (f, i) =>
      ({
        id: f.id,
        lineItemId: '',
        type: f.type,
        hours: 'hours' in f ? f.hours : null,
        rate: 'rate' in f ? f.rate : null,
        amount: 'amount' in f ? f.amount : null,
        note: f.note ?? null,
        sortOrder: i,
        createdAt: '',
        updatedAt: '',
      }) as unknown as BreakdownSubItem,
  );
}

function toSubItemForms(inputs: BreakdownSubItemInput[]): SubItemForm[] {
  return inputs.map((s): SubItemForm => {
    const base = { id: crypto.randomUUID(), type: s.type, ...(s.note ? { note: s.note } : {}) };
    if (s.amount != null) {
      return { ...base, amount: Number(s.amount) };
    }
    return { ...base, hours: Number(s.hours ?? 0), rate: Number(s.rate ?? 0) };
  });
}

export function EquipmentLineItemBody({
  subItems,
  onSubItemsChange,
  readOnly,
  lineItemDescription = null,
}: EquipmentLineItemBodyProps) {
  const catalogQuery = useEquipmentCatalog();

  // The EquipmentEditor owns its own UI state from the seed `initialSubItems`.
  // We avoid feeding regenerated UUIDs from our own onChange back into the seed
  // (that remounts inputs on every keystroke and loses focus). But we DO need
  // to re-seed when the parent replaces subItems externally — undo/redo,
  // version restore, etc. — by remounting via a bumped key.
  const lastEmittedRef = useRef<SubItemForm[] | null>(null);
  const seedRef = useRef<BreakdownSubItem[]>(toBreakdownSubItems(subItems));
  const [seedKey, setSeedKey] = useState(0);

  useEffect(() => {
    // Echo of our own emit — keep editor state intact.
    if (subItems === lastEmittedRef.current) return;
    seedRef.current = toBreakdownSubItems(subItems);
    setSeedKey((k) => k + 1);
  }, [subItems]);

  if (catalogQuery.isLoading) {
    return <div className="p-4 text-sm text-gray-500">Loading equipment catalog…</div>;
  }
  if (catalogQuery.isError || !catalogQuery.data) {
    return (
      <div className="p-4 text-sm text-red-600">Failed to load equipment catalog.</div>
    );
  }

  const { types, serviceRates } = catalogQuery.data;

  const handleChange = (next: BreakdownSubItemInput[]) => {
    const forms = toSubItemForms(next);
    lastEmittedRef.current = forms;
    onSubItemsChange(forms);
  };

  if (readOnly) {
    return (
      <div className="pointer-events-none opacity-90">
        <EquipmentEditor
          key={seedKey}
          equipmentTypes={types}
          serviceRates={serviceRates}
          initialSubItems={seedRef.current}
          onChange={() => {}}
          readOnly
          lineItemDescription={lineItemDescription}
        />
      </div>
    );
  }

  return (
    <EquipmentEditor
      key={seedKey}
      equipmentTypes={types}
      serviceRates={serviceRates}
      initialSubItems={seedRef.current}
      onChange={handleChange}
      lineItemDescription={lineItemDescription}
    />
  );
}
